// pages/AuditLogDetailPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Meta from "components/Meta";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import {
  ArrowLeft,
  User,
  Calendar,
  Hash,
  Clock,
  AlertTriangle,
  TestTube,
  Tag,
  CheckCircle,
  RotateCcw,
} from "lucide-react";
import PageHeader from "components/PageLayout";
import { displayValueMultiline } from "utilities/displayValue";

export default function AuditLogDetailPage({ log: logProp }) {
  const { logId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = useEffectiveCompanyId();

  const [log, setLog] = useState(logProp || null);
  const [loading, setLoading] = useState(!logProp);
  const [error, setError] = useState(null);

  const headerMeta = useMemo(() => {
    if (!log) return [];
    const displayDate = log.createdAt
      ? new Date(log.createdAt).toLocaleDateString("en-US", {
          year: "numeric", month: "short", day: "numeric",
        })
      : log.timestamp;
    return [
      log.changeType && { label: "Change Type", value: log.changeType },
      log.changeCategory && { label: "Category", value: log.changeCategory },
      log.implementer?.name && { label: "Implementer", value: log.implementer.name },
      displayDate && { label: "Date", value: displayDate },
    ].filter(Boolean);
  }, [log]);

  const statusMeta = useMemo(() => statusBadge(log?.status), [log?.status]);

  const fetchLog = useCallback(async () => {
    if (logProp || !logId) return;
    try {
      setLoading(true);
      setError(null);

      const authUser = user
        ? { id: user.id, accessToken: user.accessToken }
        : undefined;
      const url = apiRoutes.getAuditLogByID(logId);
      const res = await makeApiRequest("get", url, authUser, companyId);

      const record = res?.data ?? res;
      setLog(record);
    } catch (err) {
      console.error("Failed to fetch audit log", err);
      setError("Could not load this log entry.");
    } finally {
      setLoading(false);
    }
  }, [logId, logProp, user?.id, user?.accessToken]);

  useEffect(() => {
    fetchLog();
  }, [fetchLog]);

  useEffect(() => {
    if (log?.title) {
      document.title = `${log.title} - Change Log`;
    }
  }, [log?.title]);

  return (
    <div className="min-h-screen">
      <Meta
        title={log?.title ? `${log.title} - Change Log` : "Change Log Detail"}
      />

      <PageHeader
        title={log?.entityName || log?.title || (loading ? "Loading..." : "Change Log")}
        badge={
          log?.status ? (
            <span className={["inline-flex items-center gap-2", statusMeta.cls].join(" ")}>
              {statusMeta.icon}
              <span className="text-sm font-medium">{log.status}</span>
            </span>
          ) : null
        }
        meta={headerMeta}
        actions={
          <>
            {log?.parentLogId && (
              <button
                onClick={() => navigate(`/audit/${log.parentLogId}`)}
                className="btn btn-ghost"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Bulk Log
              </button>
            )}
            <button onClick={() => navigate(-1)} className="btn btn-ghost">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </button>
          </>
        }
      />

      <main className="container mx-auto py-8">
        {loading && <LoadingSkeleton />}
        {error && (
          <div className="surface rounded-lg p-6 ring-1 ring-destructive/30 bg-destructive/5">
            <div className="text-destructive text-sm">{error}</div>
          </div>
        )}
        {!loading && !error && log && (
          <AuditLogDetailBody log={log} />
        )}
      </main>
    </div>
  );
}

/* ---------------------- Extracted Body ---------------------- */
function AuditLogDetailBody({ log }) {
  const displayDate = log.createdAt
    ? new Date(log.createdAt).toLocaleDateString("en-US", {
        year: "numeric", month: "short", day: "numeric",
      })
    : log.timestamp;

  return (
    <div className="space-y-10">
      {/* Change Details */}
      {(
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <h3 className="section-heading mb-3">Change Details</h3>
          {(log.change?.before || log.change?.after) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ChangeBlock label="Before" value={log.change?.before} tone="destructive" />
              <ChangeBlock label="After" value={log.change?.after} tone="success" />
            </div>
          )}
          {log.change?.description && (
            <div className="mt-4">
              <div className="text-sm font-medium text-muted-foreground mb-1">Description</div>
              <p className="text-sm text-foreground">{log.change.description}</p>
            </div>
          )}
          {log.details && Object.keys(log.details).length > 0 && (
            <div className="mt-4">
              <div className="text-sm font-medium text-muted-foreground mb-2">Details</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(log.details).map(([key, value]) => (
                  <div key={key} className="text-sm">
                    <span className="font-medium text-muted-foreground">{key}: </span>
                    <span className="text-foreground">
                      {typeof value === "object" ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Core Information */}
      {(
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <h3 className="section-heading mb-3">Core Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
            <PropertyRow icon={<Tag />} label="Change Type" value={log.changeType} />
            <PropertyRow icon={<Hash />} label="Category" value={log.changeCategory} />
            <PropertyRow
              icon={<User />}
              label="Implementer"
              value={
                log.implementer
                  ? `${log.implementer.name}${log.implementer.email ? ` (${log.implementer.email})` : ""}`
                  : "-"
              }
            />
            <PropertyRow icon={<Calendar />} label="Date" value={displayDate} />
            {log.entityName && (
              <PropertyRow icon={<Tag />} label="Entity" value={log.entityName} />
            )}
          </div>
        </section>
      )}

      {/* Tags */}
      {Array.isArray(log.tags) && log.tags.length > 0 && (
        <section className="surface rounded-xl p-6 ring-1 ring-border">
          <h3 className="section-heading mb-3">Tags</h3>
          <div className="flex flex-wrap gap-2 mt-2">
            {log.tags.map((tag) => (
              <span
                key={tag}
                className="surface rounded px-2 py-1 text-xs text-muted-foreground ring-1 ring-border"
              >
                {tag}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------------------------- Helpers ---------------------------- */
function statusBadge(status) {
  switch (status) {
    case "Live":
      return { cls: "badge badge-success", icon: <CheckCircle className="w-4 h-4" /> };
    case "Testing":
      return { cls: "badge badge-info", icon: <TestTube className="w-4 h-4" /> };
    case "Rollback Required":
    case "Rollback":
      return { cls: "badge badge-destructive", icon: <RotateCcw className="w-4 h-4" /> };
    case "Pending":
      return { cls: "badge badge-warning", icon: <Clock className="w-4 h-4" /> };
    case "Archived":
      return { cls: "badge badge-muted", icon: <AlertTriangle className="w-4 h-4" /> };
    default:
      return { cls: "badge badge-muted", icon: <AlertTriangle className="w-4 h-4" /> };
  }
}

function PropertyRow({ icon, label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start py-2 border-b border-border last:border-b-0">
      <div className="flex-shrink-0 mr-3 text-muted-foreground">{icon}</div>
      <div>
        <div className="text-sm font-medium text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground">{value}</div>
      </div>
    </div>
  );
}

function ChangeBlock({ label, value, tone }) {
  const toneMap = {
    destructive: {
      border: "ring-1 ring-destructive/40",
      bg: "bg-destructive/5",
      text: "text-destructive",
    },
    success: {
      border: "ring-1 ring-success/40",
      bg: "bg-success/5",
      text: "text-success",
    },
  };
  const t = toneMap[tone] || toneMap.destructive;

  return (
    <div className={`surface rounded-lg p-4 ${t.bg} ${t.border}`}>
      <div className="text-xs font-medium text-muted-foreground mb-2">{label}</div>
      <code
        className={`block font-mono text-sm rounded px-2 py-1 whitespace-pre-wrap break-words ${t.text}`}
      >
        {displayValueMultiline(value)}
      </code>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      <div className="surface rounded-lg p-6 ring-1 ring-border">
        <div className="h-5 w-40 bg-muted rounded mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-4 w-56 bg-muted rounded" />
          </div>
          <div className="space-y-3">
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-4 w-56 bg-muted rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}
